// src/pages/AcrisPartiesModal.jsx
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { BASE_URL } from "../utils";

const labelCls = "text-[11px] font-semibold text-slate-600 tracking-wide";
const inputCls =
  "w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300";

const safeArr = (v) => (Array.isArray(v) ? v : []);
const toStr = (v) => (v == null ? "" : String(v));

function normalizeParty(p = {}) {
  const phones = safeArr(p.phones).map(toStr).filter(Boolean);
  const emails = safeArr(p.emails).map(toStr).filter(Boolean);

  const onePhone = toStr(p.phone).trim();
  const oneEmail = toStr(p.email).trim();

  const mergedPhones = [...new Set([...(onePhone ? [onePhone] : []), ...phones])];
  const mergedEmails = [...new Set([...(oneEmail ? [oneEmail] : []), ...emails])];

  const rowsCount = Math.max(1, Math.max(mergedPhones.length, mergedEmails.length));
  const rows = Array.from({ length: rowsCount }).map((_, i) => ({
    phone: mergedPhones[i] || "",
    email: mergedEmails[i] || "",
  }));

  return {
    name: toStr(p.name),
    address_1: toStr(p.address_1),
    city: toStr(p.city),
    state: toStr(p.state),
    zip: toStr(p.zip),
    relationship: toStr(p.relationship),
    contactRows: rows,
    raw: p.raw || p,
  };
}

function partyToPayload(p) {
  const phones = safeArr(p.contactRows)
    .map((r) => toStr(r.phone).trim())
    .filter(Boolean);

  const emails = safeArr(p.contactRows)
    .map((r) => toStr(r.email).trim())
    .filter(Boolean);

  return {
    name: toStr(p.name).trim() || null,
    address_1: toStr(p.address_1).trim() || null,
    city: toStr(p.city).trim() || null,
    state: toStr(p.state).trim() || null,
    zip: toStr(p.zip).trim() || null,
    relationship: toStr(p.relationship).trim() || null,

    // compatibility fields
    phone: phones[0] || null,
    email: emails[0] || null,

    phones,
    emails,

    raw: p.raw || {},
  };
}

export default function AcrisPartiesModal({ open, record, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [parties, setParties] = useState([]);

  useEffect(() => {
    if (!open) return;
    const initial = safeArr(record?.acris_parties_json).map(normalizeParty);
    setParties(
      initial.length
        ? initial
        : [
            normalizeParty({
              name: "",
              address_1: "",
              city: "",
              state: "",
              zip: "",
              relationship: "",
              phones: [],
              emails: [],
            }),
          ]
    );
  }, [open, record]);

  const updateParty = (idx, field, value) => {
    setParties((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  const updateContactRow = (pIdx, rowIdx, field, value) => {
    setParties((prev) =>
      prev.map((p, i) => {
        if (i !== pIdx) return p;
        const rows = safeArr(p.contactRows).map((r, ri) =>
          ri === rowIdx ? { ...r, [field]: value } : r
        );
        return { ...p, contactRows: rows };
      })
    );
  };

  const addContactRow = (pIdx) => {
    setParties((prev) =>
      prev.map((p, i) => {
        if (i !== pIdx) return p;
        return { ...p, contactRows: [...safeArr(p.contactRows), { phone: "", email: "" }] };
      })
    );
  };

  const removeContactRow = (pIdx, rowIdx) => {
    setParties((prev) =>
      prev.map((p, i) => {
        if (i !== pIdx) return p;
        const rows = safeArr(p.contactRows).filter((_, ri) => ri !== rowIdx);
        return { ...p, contactRows: rows.length ? rows : [{ phone: "", email: "" }] };
      })
    );
  };

  const addParty = () => {
    setParties((prev) => [
      ...prev,
      normalizeParty({
        name: "",
        address_1: "",
        city: "",
        state: "",
        zip: "",
        relationship: "",
        phones: [],
        emails: [],
      }),
    ]);
  };

  const removeParty = (idx) => setParties((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    const id = record?.id;
    if (!id) return;

    const payload = parties.map(partyToPayload);

    setSaving(true);
    try {
      await axios.put(`${BASE_URL}/api/legacy/${id}/acris_parties`, { parties: payload });
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error("save acris parties error", e);
      alert(e?.response?.data?.message || "Failed to save parties");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-6xl p-4 max-h-[85vh] overflow-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-lg font-semibold">Acris Parties (parsed)</div>
            <div className="text-xs text-slate-500">
              You can edit relationship, multiple phone/email for each member and Save
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="ui-btn"
            >
              Close
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`ui-btn ui-btn-primary ${saving ? "opacity-60" : ""}`}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        <div className="mb-3">
          <button
            onClick={addParty}
            className="ui-btn"
          >
            + Add Party
          </button>
        </div>

        <div className="overflow-auto border border-slate-200 rounded-lg bg-white">
          <table className="w-full text-sm min-w-[1450px]">
            <thead className="bg-[var(--surface-muted)] border-b border-[var(--border)]">
              <tr>
                <th className="text-left p-3 w-[260px]">Name</th>
                <th className="text-left p-3 w-[240px]">Address</th>
                <th className="text-left p-3 w-[170px]">City</th>
                <th className="text-left p-3 w-[220px]">Relationship</th>

                {/* Give contacts more real space */}
                <th className="text-left p-3 w-[560px]">Contacts (phone / email)</th>

                <th className="text-center p-3 w-[110px]">Remove</th>
              </tr>
            </thead>

            <tbody>
              {parties.map((p, idx) => (
                <tr key={idx} className="border-b border-slate-100 align-top">
                  <td className="p-3">
                    <div className={labelCls}>Name</div>
                    <input
                      className={inputCls}
                      value={p.name}
                      onChange={(e) => updateParty(idx, "name", e.target.value)}
                      placeholder="Name"
                    />
                  </td>

                  <td className="p-3">
                    <div className={labelCls}>Address</div>
                    <input
                      className={inputCls}
                      value={p.address_1}
                      onChange={(e) => updateParty(idx, "address_1", e.target.value)}
                      placeholder="Address"
                    />
                  </td>

                  <td className="p-3">
                    <div className={labelCls}>City / State</div>
                    <input
                      className={inputCls}
                      value={p.city}
                      onChange={(e) => updateParty(idx, "city", e.target.value)}
                      placeholder="City"
                    />
                    <div className="flex gap-2 mt-2">
                      <input
                        className={inputCls}
                        value={p.state}
                        onChange={(e) => updateParty(idx, "state", e.target.value)}
                        placeholder="State"
                      />
                      <input
                        className={inputCls}
                        value={p.zip}
                        onChange={(e) => updateParty(idx, "zip", e.target.value)}
                        placeholder="Zip"
                      />
                    </div>
                  </td>

                  <td className="p-3">
                    <div className={labelCls}>Relationship</div>
                    <input
                      className={inputCls}
                      value={p.relationship}
                      onChange={(e) => updateParty(idx, "relationship", e.target.value)}
                      placeholder="e.g. spouse, daughter"
                    />
                  </td>

                  {/* ✅ PROFESSIONAL CONTACTS UI */}
                  <td className="p-3">
                    <div className="rounded-lg border border-slate-200 bg-[var(--surface-muted)]/60 p-2">
                      <div className="space-y-2">
                        {safeArr(p.contactRows).map((r, ri) => {
                          const onlyOne = safeArr(p.contactRows).length <= 1;
                          return (
                            <div
                              key={ri}
                              className="grid grid-cols-[260px_1fr_34px] gap-2 items-center"
                            >
                              {/* Phone */}
                              <input
                                className={`${inputCls} font-mono`}
                                value={r.phone}
                                onChange={(e) => updateContactRow(idx, ri, "phone", e.target.value)}
                                placeholder="Phone"
                                autoComplete="off"
                              />

                              {/* Email - expands */}
                              <input
                                className={inputCls}
                                value={r.email}
                                onChange={(e) => updateContactRow(idx, ri, "email", e.target.value)}
                                placeholder="Email"
                                autoComplete="off"
                              />

                              {/* Remove row */}
                              <button
                                type="button"
                                disabled={onlyOne}
                                onClick={() => removeContactRow(idx, ri)}
                                title={onlyOne ? "At least one row is required" : "Remove this contact row"}
                                className={`h-9 w-9 rounded-md border flex items-center justify-center ${
                                  onlyOne
                                    ? "bg-white text-slate-300 border-slate-200 cursor-not-allowed"
                                    : "bg-white text-red-600 border-slate-200 hover:bg-red-50"
                                }`}
                              >
                                ✕
                              </button>
                            </div>
                          );
                        })}

                        <div className="pt-1">
                          <button
                            type="button"
                            onClick={() => addContactRow(idx)}
                            className="ui-btn text-sm inline-flex items-center gap-2"
                            title="Add another phone/email row"
                          >
                            <span className="inline-flex items-center justify-center h-5 w-5 rounded bg-blue-600 text-white text-xs">
                              +
                            </span>
                            Add contact row
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="p-3 text-center">
                    <button
                      type="button"
                      className="px-3 py-1.5 border border-slate-200 rounded-md text-xs text-red-600 bg-white hover:bg-red-50"
                      onClick={() => removeParty(idx)}
                      title="Remove party"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}

              {parties.length === 0 && (
                <tr>
                  <td className="p-4 text-slate-500" colSpan={6}>
                    No parties
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="ui-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`ui-btn ui-btn-primary ${saving ? "opacity-60" : ""}`}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
